import { type HttpClient, type RouteGenerator } from "silver";
import { type Book } from "core/Data/Book";
import { type MyonApiResponse, isMyonApiSuccess } from "shared/Api/MyonApiResponse";

export type BookSearchField = "title"|"author"|"series"|"publisher"|"description";
export type BookSearchLength = "any"|"short"|"medium"|"long"|"epic";

export function isBookSearchField(value: any): value is BookSearchField {
	return value === "title" || value === "author" || value === "series" || value === "publisher" || value === "description";
}
export function isBookSearchLength(value: any): value is BookSearchLength {
	return value === "any" || value === "short" || value === "medium" || value === "long" || value === "epic";
}

export interface BookSearchQuery {
	search?: BookSearchField[];
	language?: Array<number | "bi">;
	length?: BookSearchLength;
	guidedReading?: [string, string];
	gradeLevel?: [number, number];
	lexile?: [number, number];
	atos?: [ number, number ];
	fiction?: boolean;
	audio?: 1|0;
	text?: string;
}

export type SearchBooksAutoCompleteResult = {
	type: "book"|"series"|"author"|"category"|"publisher"
	similarity: number;
	title: string;
	url: string;
};


export function getBookSearchQueryFromObject(query: any): BookSearchQuery|null {
	const out: BookSearchQuery = { };

	function paramsToArray(obj: { [ key: string ]: string }): string[] {
		const out: string[] = [];
		for (let i = 0; String(i) in obj; i++) {
			out.push(obj[i]);
		}
		return out;
	}

	const text = query.text;
	if (text) {
		/**
		 * @TODO this should be handled by the router... or something?
		 * competing standards: RFC 3986 wants spaces as %20, RFC 1866 wants them as '+'.
		 * PHP is storing the calling url in 1866 with http_build_query, but we're bulding
		 * it in JS using 3986. An irritation.
		*/
		out.text = String(text).replace(/\+/g, " ");
	}

	const search = paramsToArray(query.search ?? {}).filter(isBookSearchField);
	if (search) {
		out.search = search;
	}

	const languages = paramsToArray(query.language ?? {});
	if (languages.length) {
		const language: BookSearchQuery["language"] = [];
		languages.forEach(l => {
			if (l == "bi") {
				language.push(l);
			} else if (parseInt(l, 10)) {
				language.push(Number(l));
			}
		});
		out.language = language;
	}

	const length = query.length;
	if (isBookSearchLength(length)) {
		out.length = length;
	}

	const guidedReading = paramsToArray(query.guidedReading ?? {});
	if (guidedReading.length === 2) {
		out.guidedReading = [ guidedReading[0], guidedReading[1] ];
	}

	const gradeLevel = paramsToArray(query.gradeLevel ?? {}).map(Number);
	if (gradeLevel.length === 2) {
		out.gradeLevel = [ gradeLevel[0], gradeLevel[1] ];
	}

	const lexile = paramsToArray(query.lexile ?? {}).map(Number);
	if (lexile.length === 2) {
		out.lexile = [ lexile[0], lexile[1] ];
	}

	const atos = paramsToArray(query.atos ?? {}).map(Number);
	if (atos.length === 2) {
		out.atos = [ atos[0], atos[1] ];
	}

	const fiction = query.fiction;
	if (fiction !== undefined) {
		out.fiction = Boolean(fiction);
	}

	const audio = query.audio;
	if (audio !== undefined) {
		out.audio = audio ? 1 : 0; // @TODO why not bool here?
	}

	if (Object.keys(out).length === 0) {
		return null;
	}

	return out;
}


export class SearchBooksService {
	constructor(
		private router: RouteGenerator,
		private httpClient: HttpClient
	) { }

	public async search(params: BookSearchQuery): Promise<number[]> {
		const url = this.router.generate("api\\books", "search", params as any, "json");
		const response: MyonApiResponse<number[]> = await this.httpClient.get(url);
		if (! isMyonApiSuccess(response)) {
			console.error(response);
			throw new Error("Search failed");
		}
		return response.result;
	}

	public async autocomplete(text: string, abortController?: AbortController): Promise<SearchBooksAutoCompleteResult[]> {
		text = text.replace(/[+_\-><()~*@]+/, " ");
		const url = this.router.generate("api\\books", "autocomplete", { text }, "json");
		const response: MyonApiResponse<SearchBooksAutoCompleteResult[]> = await this.httpClient.get(url, { abortController });
		if (!isMyonApiSuccess(response)) {
			console.error(response);
			throw new Error("Autocomplete search failed");
		}
		return response.result;
	}

	public getSearchURL(params: BookSearchQuery): string {
		return this.router.generate("library\\search", null, params as any);
	}

	public getSimilarLexileSearchURL(book: Book): string|null {
		if (! book.lexileLevel) {
			return null;
		}
		return this.getSearchURL({ lexile: [ book.lexileLevel - 50, book.lexileLevel + 100 ] });
	}

	public getGradeLevelSearchURL(book: Book): string|null {
		if (!book.minUserGradeLevelId || !book.maxUserGradeLevelId) {
			return null;
		}
		return this.getSearchURL({ gradeLevel: [ book.minUserGradeLevelId, book.maxUserGradeLevelId ] });
	}

	public getBookPublisherSearchURL(book: Book): string|null {
		if (!book.bookPublisherTitle) {
			return null;
		}
		return this.router.generate("library\\search", null, { publisher: book.bookPublisherId });
	}

	public getBookSeriesSearchURL(book: Book): string|null {
		if (!book.bookSeriesId) {
			return null;
		}
		return this.router.generate("library\\search", null, { series: book.bookSeriesId });
	}

	public getBookGuidedReadingLevelSearchURL(book: Book): string|null {
		if (!book.guidedReadingLevel) {
			return null;
		}
		return this.getSearchURL({ guidedReading: [
			book.guidedReadingLevel,
			String.fromCharCode(book.guidedReadingLevel.charCodeAt(0) + 1)
		] });
	}

	public getBookAudioRuntimeSearchURL(book: Book): string|null {
		if (!book.audioRuntimeFriendly) {
			return null;
		}
		return this.getSearchURL({
			audio:  book.audioRuntimeSecs ? 1 : 0,
			length: (!book.audioRuntimeSecs ? undefined :
				((book.audioRuntimeSecs <= 15 * 60) ? "short" :
					((book.audioRuntimeSecs <= 30 * 60) ? "medium" :
						((book.audioRuntimeSecs <= 60 * 60) ? "long" :
							"epic"))))
		});
	}

	public getBookPageCountSearchURL(book: Book): string|null {
		if (!book.pagecount) {
			return null;
		}
		return this.getSearchURL({
			length: (book.pagecount <= 24) ? "short" :
				((book.pagecount <= 40) ? "medium" :
					((book.pagecount <= 100) ? "long" :
						"epic"))
		});
	}

	public getSimilarATOSSearchURL(book: Book): string|null {
		if (! book.arQuizLevel) {
			return null;
		}
		return this.getSearchURL({ atos: [ ((book.arQuizLevel) - 0.2), ((book.arQuizLevel) + 0.2) ] });
	}
}
